import Box from "@mui/material/Box";
import type { ReactNode } from "react";

import { EdgeMask, Marquee } from "./Marquee";
import { NOT_WIDE, TINY, marketingPalette } from "./tokens";

const ASSETS = "/marketing/home";

/**
 * The customers' logos the published site scrolls under "Webshare is trusted
 * by…", the same six wherever the strip is set. `size` is the published width
 * class: the strip is not one scale.
 */
const CUSTOMER_LOGOS = [
  { src: `${ASSETS}/customer-1.svg`, size: "smaller" },
  { src: `${ASSETS}/customer-vendisys.svg`, size: "smaller" },
  { src: `${ASSETS}/customer-2.svg`, size: "regular" },
  { src: `${ASSETS}/customer-extenship.svg`, size: "regular" },
  { src: `${ASSETS}/customer-knet.svg`, size: "regular" },
  { src: `${ASSETS}/customer-buzz.svg`, size: "small" },
] as const;

/** Three widths, hand-set per logo on the published strip. */
const LOGO_WIDTH = {
  regular: { width: "8rem", [TINY]: { width: "6rem" } },
  smaller: { width: "7rem", [TINY]: { width: "5rem" } },
  small: { width: "5rem", pb: "4px", [TINY]: { width: "4rem" } },
};

/**
 * `.logos_wrap`: the line naming the customer count beside the logos
 * scrolling under their white edge masks, stacked below the wide step.
 */
export default function CustomerLogos({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: "32px",
        [NOT_WIDE]: {
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "16px",
        },
        [TINY]: { alignItems: "center" },
      }}
    >
      <Box
        sx={{
          flex: "none",
          maxWidth: "22ch",
          color: marketingPalette.ink,
          fontSize: "1.3rem",
          lineHeight: "130%",
          WebkitTextStrokeWidth: "0.5px",
          [NOT_WIDE]: { maxWidth: "none", fontSize: "1.2rem" },
          [TINY]: { textAlign: "center", fontSize: "0.9rem" },
        }}
      >
        {children}
      </Box>
      <Box sx={{ position: "relative", overflow: "hidden", width: 1 }}>
        <Marquee duration={40} reverse gap="0px">
          {CUSTOMER_LOGOS.map((logo) => (
            <Box
              key={logo.src}
              sx={{ flex: "none", px: "2rem", [TINY]: { px: "0.8rem" } }}
            >
              <Box
                component="img"
                src={logo.src}
                alt=""
                loading="lazy"
                sx={{
                  // A block, so the item is the image's own height and not
                  // an inline line box seven pixels taller.
                  display: "block",
                  opacity: 0.7,
                  ...LOGO_WIDTH[logo.size],
                }}
              />
            </Box>
          ))}
        </Marquee>
        <EdgeMask
          angle="90deg"
          ground="#FFFFFF"
          sx={{
            inset: "0 auto 0 0",
            width: "12rem",
            [TINY]: { width: "5rem" },
          }}
        />
        <EdgeMask
          angle="254deg"
          ground="#FFFFFF"
          sx={{
            inset: "0 0 0 auto",
            width: "12rem",
            [TINY]: { width: "5rem" },
          }}
        />
      </Box>
    </Box>
  );
}
